import { Suspense, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import type { ContentRefOpenAPI } from '@gitbook/api-client';
import { openapiOperation, openapiSchemas, openapiWebhook } from '@gitbook/doc-core';
import type { Block } from '@gitbook/slate';
import {
    Button,
    Description,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    Form,
    Heading,
    LoadingPanel,
    Submit,
    toast,
    useDialogClose,
} from '@gitbook/spine-react';
import { assert, assertNever } from '@gitbook/util-ts';

import { apiQuery, apiQueryClient, useCurrentOrganizationId } from '../../../state';
import '../../OpenAPI/react-openapi.css';

import { APIErrorBoundary } from '../../APIErrorBoundary';
import {
    OpenAPIComputedIncentiveAlert,
    OpenAPISpecPicker,
    OpenAPIWaitForSpec,
} from '../../OpenAPI';
import { OpenAPIBlockSelection } from './insertion/OpenAPIBlockSelection';
import type { InsertOpenAPIBlockDialogFormInputs } from './insertion/types';

export type InsertOpenAPIBlockDialogProps = {
    /**
     * Callback to insert the selected OpenAPI blocks into the editor.
     */
    onInsert: (blocks: Block[], sourceRef: ContentRefOpenAPI) => void;
};
export function InsertOpenAPIBlockDialog(props: InsertOpenAPIBlockDialogProps) {
    const { onInsert } = props;
    const organizationId = useCurrentOrganizationId();
    if (!organizationId) {
        return null;
    }
    return (
        <Dialog size="auto">
            <DialogHeader className="w-screen max-w-xl">
                <Heading>Insert OpenAPI blocks</Heading>
                <Description>
                    Insert OpenAPI blocks in your document from an OpenAPI specification.
                </Description>
            </DialogHeader>
            <InsertOpenAPIBlockForm organizationId={organizationId} onInsert={onInsert} />
        </Dialog>
    );
}

type Step = 'select_spec' | 'select_blocks';

function InsertOpenAPIBlockForm(
    props: {
        organizationId: string;
    } & Pick<InsertOpenAPIBlockDialogProps, 'onInsert'>
) {
    const { organizationId, onInsert } = props;
    const [step, setStep] = useState<Step>('select_spec');
    const form = useForm<InsertOpenAPIBlockDialogFormInputs>({
        defaultValues: {
            specSlug: null,
            operations: [],
            schemas: [],
            webhooks: [],
        },
    });
    const specSlug = form.watch('specSlug');
    const closeDialog = useDialogClose();
    const onSubmit: SubmitHandler<InsertOpenAPIBlockDialogFormInputs> = (data) => {
        if (step === 'select_spec') {
            setStep('select_blocks');
            return;
        }
        assert(data.specSlug, "specSlug should be set in 'select_blocks' step");
        const sourceRef: ContentRefOpenAPI = {
            kind: 'openapi',
            spec: data.specSlug,
        };
        if (
            data.operations.length === 0 &&
            data.schemas.length === 0 &&
            data.webhooks.length === 0
        ) {
            toast('Please select at least one operation, schema or webhook');
            return;
        }

        onInsert(
            [
                ...data.operations.map((operation) => {
                    const { method, path } = parseOperation(operation);
                    return openapiOperation.createBlockWithRef({
                        ref: sourceRef,
                        path,
                        method,
                    });
                }),
                ...(data.schemas.length
                    ? [
                          openapiSchemas.createOpenAPISchemasBlockWithRef({
                              ref: sourceRef,
                              schemas: data.schemas,
                          }),
                      ]
                    : []),
                ...data.webhooks.map((webhook) => {
                    const { method, path: name } = parseOperation(webhook);
                    return openapiWebhook.createOpenAPIWebhookBlockWithRef({
                        ref: sourceRef,
                        name,
                        method,
                    });
                }),
            ],
            sourceRef
        );
        closeDialog();
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            {(() => {
                switch (step) {
                    case 'select_spec': {
                        return (
                            <DialogBody className="w-screen max-w-xl">
                                <Suspense fallback={<LoadingPanel size="xlarge" />}>
                                    <OpenAPISpecPicker
                                        form={form}
                                        organizationId={props.organizationId}
                                        name="specSlug"
                                        analyticsContext={{
                                            triggerLocation: 'insert-block-dialog',
                                        }}
                                    />
                                    {specSlug && <OpenAPIComputedIncentiveAlert className="mt-4" />}
                                </Suspense>
                            </DialogBody>
                        );
                    }
                    case 'select_blocks': {
                        assert(specSlug, "specSlug should be set in 'select_blocks' step");
                        return (
                            <DialogBody
                                scrollable={false}
                                className="flex h-[calc(100vh-280px)] w-screen max-w-screen-xl flex-col"
                            >
                                <APIErrorBoundary
                                    fallback={{
                                        404: ({ resetError }) => (
                                            <OpenAPIWaitForSpec
                                                organizationId={organizationId}
                                                specSlug={specSlug}
                                                onReady={() => {
                                                    // Reset the query to fetch the latest content.
                                                    apiQueryClient.removeQueries({
                                                        queryKey: apiQuery(
                                                            'getLatestOpenAPISpecVersionContent',
                                                            {
                                                                organizationId,
                                                                specSlug,
                                                            }
                                                        ).queryKey,
                                                    });
                                                    resetError();
                                                }}
                                            />
                                        ),
                                    }}
                                >
                                    <Suspense fallback={<LoadingPanel />}>
                                        <OpenAPIBlockSelection
                                            form={form}
                                            organizationId={organizationId}
                                            specSlug={specSlug}
                                        />
                                    </Suspense>
                                </APIErrorBoundary>
                            </DialogBody>
                        );
                    }
                    default:
                        assertNever(step);
                }
            })()}
            <DialogFooter>
                {step === 'select_spec' ? (
                    <DialogCloseButton />
                ) : (
                    <Button
                        kind="secondary"
                        label="Change specification"
                        onPress={() => {
                            switch (step) {
                                case 'select_blocks': {
                                    setStep('select_spec');
                                    break;
                                }
                                default: {
                                    assertNever(step);
                                }
                            }
                        }}
                    />
                )}
                <Submit label={step === 'select_blocks' ? 'Insert blocks' : 'Continue'} />
            </DialogFooter>
        </Form>
    );
}

const SEPARATOR = '$$//$$';

function parseOperation(value: string) {
    const [method, path] = value.split(SEPARATOR);
    return { method, path };
}
